import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectLanguage } from '../redux/Slices/busSlice';
import { Link } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';

// Import CSS for react-pdf
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Import assets
import logo from '../../components/assets/logos/logo2.PNG';
import englishGuide from '../assets/giudes/English.pdf';
import hindiGuide from '../assets/giudes/Hindi.pdf';
import { FaHome } from 'react-icons/fa';

// Standardized Worker Setup using CDN
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

const UserGuide = () => {
  const { t } = useTranslation();
  const currentLanguage = useSelector(selectLanguage);
  const [isMobile, setIsMobile] = useState(false);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [pdfContainerWidth, setPdfContainerWidth] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  const pdfContainerRef = useRef(null);

  // Check screen size for mobile layout
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Get the correct PDF based on language
  const getPdfGuide = () => {
    return currentLanguage === 'hi' ? hindiGuide : englishGuide;
  };

  // Update PDF container width for responsive scaling
  const updateContainerWidth = useCallback(() => {
    if (pdfContainerRef.current) {
      // Subtract padding (20px on mobile, 40px on desktop) for better fit
      const paddingAdjustment = isMobile ? 20 : 40;
      const width = pdfContainerRef.current.clientWidth - paddingAdjustment;
      setPdfContainerWidth(width > 0 ? width : 300); // fallback width
    }
  }, [isMobile]);

  // Set up resize observer to adapt width on window/fullscreen changes
  useEffect(() => {
    updateContainerWidth();
    window.addEventListener('resize', updateContainerWidth);
    
    // Also observe resize of the container itself (for fullscreen changes)
    const resizeObserver = new ResizeObserver(updateContainerWidth);
    if (pdfContainerRef.current) {
      resizeObserver.observe(pdfContainerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', updateContainerWidth);
      resizeObserver.disconnect();
    };
  }, [updateContainerWidth]);

  // Listen for fullscreen change events to update button state
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
      // Force width recalculation when fullscreen changes
      setTimeout(updateContainerWidth, 100);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [updateContainerWidth]);

  // PDF load handlers
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setLoading(false);
    setError(false);
  }

  function onDocumentLoadError(err) {
    console.error('Error loading PDF:', err);
    setLoading(false);
    setError(true);
  }

  // Navigation
  const goToPrevPage = () => {
    setPageNumber(prevPage => Math.max(prevPage - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prevPage => Math.min(prevPage + 1, numPages));
  };

  // Download PDF
  const handleDownload = () => {
    const pdfUrl = getPdfGuide();
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = currentLanguage === 'hi' ? 'UserGuide_Hindi.pdf' : 'UserGuide_English.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Toggle fullscreen mode on the PDF container
  const toggleFullscreen = () => {
    if (!pdfContainerRef.current) return;
    
    if (!document.fullscreenElement) {
      pdfContainerRef.current.requestFullscreen().catch(err => {
        console.error(`Fullscreen error: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Styles (same as original with minor additions for fullscreen flexibility)
  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      paddingBottom: '40px',
    },
    header: {
      background: 'linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%)',
      padding: isMobile ? '10px 20px' : '15px 40px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
      borderBottom: '3px solid #ffeb3b',
      marginBottom: '20px',
    },
    headerContent: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '1200px',
      margin: '0 auto',
    },
    logoLink: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
    },
    logoImage: {
      height: isMobile ? '45px' : '60px',
      width: 'auto',
      borderRadius: '8px',
    },
    backButton: {
      background: '#ff5722',
      color: 'white',
      border: 'none',
      padding: isMobile ? '8px 15px' : '10px 20px',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: isMobile ? '14px' : '16px',
      fontWeight: 'bold',
      textDecoration: 'none',
    },
    mainContent: {
      padding: isMobile ? '10px' : '20px',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    guideTitle: {
      textAlign: 'center',
      color: '#333',
      marginBottom: '20px',
      fontSize: isMobile ? '1.5rem' : '2.2rem',
      fontWeight: '700',
    },
    pdfContainer: {
      background: 'white',
      borderRadius: '12px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.15)',
      padding: isMobile ? '10px' : '30px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: '600px',
      width: '100%',
      boxSizing: 'border-box',
    },
    controls: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '12px',
      marginTop: '25px',
      background: '#f8f9fa',
      padding: '10px 20px',
      borderRadius: '50px',
      boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
      flexWrap: 'wrap',
    },
    navButton: {
      background: '#ff5722',
      color: 'white',
      border: 'none',
      padding: '8px 18px',
      borderRadius: '20px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: 'bold',
      transition: 'opacity 0.2s',
    },
    pageInfo: {
      fontSize: '14px',
      color: '#444',
      fontWeight: '600',
    },
    loadingContainer: {
      textAlign: 'center',
      padding: '100px 20px',
    },
    spinner: {
      width: '50px',
      height: '50px',
      border: '5px solid #f3f3f3',
      borderTop: '5px solid #ff5722',
      borderRadius: '50%',
      margin: '0 auto',
      animation: 'spin 1s linear infinite',
    },
    errorContainer: {
      textAlign: 'center',
      padding: '60px 20px',
      color: '#d32f2f',
    },
    footer: {
      background: '#fff9c4',
      padding: '25px 40px',
      textAlign: 'center',
      marginTop: '50px',
      borderTop: '1px solid #ffeb3b'
    },
  };

  // Inject global keyframes and canvas styles
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      .react-pdf__Page {
        box-shadow: 0 0 8px rgba(0,0,0,0.1);
        display: flex;
        justify-content: center;
      }
      .react-pdf__Page__canvas {
        max-width: 100% !important;
        height: auto !important;
      }
      button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
      /* Ensure PDF container takes full width when in fullscreen */
      :-webkit-full-screen .pdf-fullscreen-container {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        background: white;
      }
      :fullscreen .pdf-fullscreen-container {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        background: white;
      }
    `;
    document.head.appendChild(styleSheet);
    return () => document.head.removeChild(styleSheet);
  }, []);

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerContent}>
          <Link to="/" style={styles.logoLink}>
            <img src={logo} alt="Logo" style={styles.logoImage} />
          </Link>
          <Link 
                      to="/" 
                      style={styles.homeButton}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.5)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'}
                    >
                      <FaHome /> {t('home', 'Home')}
                    </Link>
        </div>
      </header>

      <main style={styles.mainContent}>
        <h1 style={styles.guideTitle}>
          {currentLanguage === 'hi' ? 'यूज़र गाइड' : 'User Guide'}
        </h1>
        
        <div 
          ref={pdfContainerRef} 
          style={styles.pdfContainer}
          className="pdf-fullscreen-container"
        >
          {loading && (
            <div style={styles.loadingContainer}>
              <div style={styles.spinner}></div>
              <p style={{ marginTop: '20px', fontWeight: '500' }}>
                {currentLanguage === 'hi' ? 'गाइड लोड हो रहा है...' : 'Loading guide...'}
              </p>
            </div>
          )}
          
          {error && (
            <div style={styles.errorContainer}>
              <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                ⚠️ {currentLanguage === 'hi' 
                ? 'गाइड लोड करने में त्रुटि।' 
                : 'Error loading guide.'}
              </p>
              <button onClick={() => window.location.reload()} style={styles.navButton}>
                {currentLanguage === 'hi' ? 'पुनः प्रयास करें' : 'Retry'}
              </button>
            </div>
          )}
          
          <Document
            file={getPdfGuide()}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
          >
            <Page 
              pageNumber={pageNumber} 
              width={pdfContainerWidth || undefined}
              scale={!pdfContainerWidth ? (isMobile ? 0.8 : 1.2) : undefined}
              renderTextLayer={true}
              renderAnnotationLayer={true}
            />
          </Document>
          
          {numPages && !error && (
            <div style={styles.controls}>
              <button 
                onClick={goToPrevPage} 
                disabled={pageNumber <= 1} 
                style={{...styles.navButton, opacity: pageNumber <= 1 ? 0.5 : 1}}
              >
                {currentLanguage === 'hi' ? 'पिछला' : 'Prev'}
              </button>
              <span style={styles.pageInfo}>
                {pageNumber} / {numPages}
              </span>
              <button 
                onClick={goToNextPage} 
                disabled={pageNumber >= numPages} 
                style={{...styles.navButton, opacity: pageNumber >= numPages ? 0.5 : 1}}
              >
                {currentLanguage === 'hi' ? 'अगला' : 'Next'}
              </button>
              
              {/* Download Button */}
              <button 
                onClick={handleDownload}
                style={{...styles.navButton, background: '#2196f3'}}
              >
                ⬇ {currentLanguage === 'hi' ? 'डाउनलोड करें' : 'Download'}
              </button>
              
              {/* <button 
                onClick={toggleFullscreen}
                style={{...styles.navButton, background: '#4caf50'}}
              >
                {isFullscreen 
                  ? (currentLanguage === 'hi' ? 'एक्जिट फुलस्क्रीन' : 'Exit Fullscreen')
                  : (currentLanguage === 'hi' ? 'फुलस्क्रीन' : 'Fullscreen')
                }
              </button> */}
            </div>
          )}
        </div>
      </main>

      <footer style={styles.footer}>
        <p>          © {new Date().getFullYear()} {t('companyName', 'Rajdoot Explorations')}. {t('allRightsReserved', 'All rights reserved.')}
</p>
      </footer>
    </div>
  );
};

export default UserGuide;